import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Plus, Search } from 'lucide-react';
import TablaIngredientes from './TablaIngredientes';
import type { WizardFacturaFormData } from '../../schemas/facturaSchema';

interface WizardStep2Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<WizardFacturaFormData, any, any>;
  ingredientes: any[];
  categorias: any[];
  unidadesMedida: any[];
  onUpdateDetalle: (index: number, field: string, value: any) => void;
  onEliminarDetalle: (index: number) => void;
  onAgregarIngrediente: (ingredienteId: number) => void;
  onAbrirNuevoIngrediente: () => void;
  onAbrirNuevaCategoria: (index: number) => void;
  onAbrirNuevaUnidadMedida: (index: number) => void;
}

const WizardStep2Ingredientes: React.FC<WizardStep2Props> = ({
  form,
  ingredientes,
  categorias,
  unidadesMedida,
  onUpdateDetalle,
  onEliminarDetalle,
  onAgregarIngrediente,
  onAbrirNuevoIngrediente,
  onAbrirNuevaCategoria,
  onAbrirNuevaUnidadMedida,
}) => {
  const [busqueda, setBusqueda] = useState('');

  const detalles = form.watch('detalles');

  // Filtrar ingredientes por búsqueda
  const ingredientesFiltrados = ingredientes.filter((ing) =>
    ing.nombre.toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Barra de búsqueda y botón nuevo */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar ingrediente..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            aria-label="Buscar ingrediente existente"
          />
        </div>
        <button
          type="button"
          onClick={onAbrirNuevoIngrediente}
          className="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-lg
                     hover:bg-green-700 dark:hover:bg-green-600 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nuevo
        </button>
      </div>

      {/* Resultados de búsqueda */}
      {busqueda && ingredientesFiltrados.length > 0 && (
        <div
          className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-40 overflow-y-auto"
          role="listbox"
          aria-label="Resultados de búsqueda de ingredientes"
        >
          {ingredientesFiltrados.slice(0, 5).map((ing) => (
            <button
              key={ing.id}
              type="button"
              onClick={() => {
                onAgregarIngrediente(ing.id);
                setBusqueda('');
              }}
              className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700
                         border-b border-gray-200 dark:border-gray-700 last:border-b-0
                         text-gray-900 dark:text-gray-100"
              role="option"
            >
              {ing.nombre} ({ing.unidadMedida?.abreviacion || ing.unidadMedida?.nombre || ing.unidad_medida || ''})
            </button>
          ))}
        </div>
      )}

      {/* Tabla de ingredientes */}
      <div className="space-y-2">
        <TablaIngredientes
          detalles={detalles}
          categorias={categorias}
          unidadesMedida={unidadesMedida}
          onUpdateDetalle={onUpdateDetalle}
          onEliminarDetalle={onEliminarDetalle}
          onAbrirNuevaCategoria={onAbrirNuevaCategoria}
          onAbrirNuevaUnidadMedida={onAbrirNuevaUnidadMedida}
        />
        {detalles.length > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400 italic">
            💡 Tip: Si los campos se extienden, puedes deslizar horizontalmente en la tabla
          </p>
        )}
      </div>

      {/* Mensaje de error de validación */}
      {form.formState.errors.detalles && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {form.formState.errors.detalles.message}
        </p>
      )}
    </div>
  );
};

export default WizardStep2Ingredientes;
